import { inject } from '@angular/core';
import { toObservable } from '@angular/core/rxjs-interop';
import type { CanActivateFn } from '@angular/router';
import { Router } from '@angular/router';
import { filter, first, map } from 'rxjs/operators';
import { AuthSessionStore } from '../+state';
import { navigateOrReport } from '../util/navigate-or-report.util';

/**
 * Route guard ensuring that the authenticated user has the `admin` role before
 * activating a route. Intended for internal-only areas such as the backoffice.
 *
 * It waits for {@link AuthSessionStore.authenticationInitialized} to be true,
 * then checks {@link AuthSessionStore.isAdmin}. Non-admin users (including
 * unauthenticated ones) are redirected to the home route (`/`). Unauthenticated
 * users are expected to be handled upstream by {@link authGuard}; this guard is
 * a role gate on top of authentication.
 *
 * @returns An {@link import('rxjs').Observable} that resolves to `true` to allow access or `false` to block it.
 * @example
 * import { Routes } from '@angular/router';
 * import { adminGuard, authGuard } from '@indutrust/auth-data';
 *
 * export const routes: Routes = [
 *   {
 *     path: 'backoffice',
 *     canActivate: [authGuard, adminGuard],
 *     loadChildren: async () => import('@indutrust/backoffice-feature').then((m) => m.libBackofficeRoutes),
 *   },
 * ];
 */
export const adminGuard: CanActivateFn = () => {
  const authStore = inject(AuthSessionStore);
  const router = inject(Router);

  return toObservable(authStore.authenticationInitialized).pipe(
    filter((initialized): boolean => initialized),
    first(),
    map(() => {
      if (!authStore.isAdmin()) {
        navigateOrReport(router, ['/'], 'Navigation to home failed');
        return false;
      }
      return true;
    })
  );
};
